function escapeHtml(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function compactReference(value) {
  const text = String(value || "").trim();
  if (!text) {
    return "Not recorded";
  }
  return text.length > 34 ? `${text.slice(0, 16)}...${text.slice(-10)}` : text;
}

function receiptTypeLabel(type = "") {
  return String(type || "payment")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function formatReceiptMoney(amountCents, currency = "usd") {
  const amount = Number(amountCents || 0) / 100;
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: String(currency || "usd").toUpperCase()
    }).format(amount);
  } catch {
    return `${String(currency || "usd").toUpperCase()} ${amount.toFixed(2)}`;
  }
}

function formatReceiptDate(value) {
  if (!value) {
    return "Not recorded";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return String(value);
  }
  return new Intl.DateTimeFormat("en-US", {
    dateStyle: "medium",
    timeStyle: "short"
  }).format(date);
}

function normalizeReceipt(receipt = {}) {
  const amountCents = receipt.amountCents ?? receipt.amount_total ?? receipt.total ?? receipt.amount ?? 0;
  const currency = receipt.currency || receipt.lineItems?.[0]?.currency || "usd";
  const lineItems = Array.isArray(receipt.lineItems) && receipt.lineItems.length > 0
    ? receipt.lineItems
    : [{
      name: receipt.description || receiptTypeLabel(receipt.type),
      amountCents,
      currency
    }];
  return {
    ...receipt,
    amountCents,
    currency,
    lineItems
  };
}

function receiptFilename(receipt = {}) {
  const id = String(receipt.receiptId || "rangegrid-receipt")
    .replace(/[^a-z0-9_-]+/gi, "-")
    .replace(/^-+|-+$/g, "");
  return `${id || "rangegrid-receipt"}.html`;
}

function rangeDisplayName(range = {}) {
  return range.displayName || range.name || range.legalName || "RangeGrid";
}

export function renderReceiptHtml(receiptInput = {}, { range = {}, title = "Payment receipt" } = {}) {
  const receipt = normalizeReceipt(receiptInput);
  const total = formatReceiptMoney(receipt.amountCents, receipt.currency);
  const paidAt = formatReceiptDate(receipt.createdAt || receipt.paidAt || receipt.updatedAt);
  const customerEmail = receipt.email || receipt.customerEmail || receipt.donorEmail || "Not recorded";
  const rangeName = rangeDisplayName(range);
  const status = receipt.status || "recorded";
  const lineItems = receipt.lineItems.map((item) => `
    <tr>
      <td>
        <strong>${escapeHtml(item.name || item.description || receipt.description || receiptTypeLabel(item.kind || receipt.type))}</strong>
        ${item.kind ? `<span>${escapeHtml(receiptTypeLabel(item.kind))}</span>` : ""}
      </td>
      <td>${escapeHtml(formatReceiptMoney(item.amountCents ?? item.amount_total ?? receipt.amountCents, item.currency || receipt.currency))}</td>
    </tr>
  `).join("");

  return `<!doctype html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <title>${escapeHtml(title)} ${escapeHtml(receipt.receiptId || "")}</title>
  <style>
    :root {
      color: #18231c;
      background: #eef2eb;
      font-family: Arial, Helvetica, sans-serif;
    }
    body {
      margin: 0;
      background:
        linear-gradient(180deg, rgba(252, 253, 249, 0.96), rgba(238, 242, 235, 0.98));
      padding: 36px;
    }
    .receipt {
      max-width: 760px;
      margin: 0 auto;
      border: 1px solid #cbd5c9;
      border-radius: 12px;
      background: #fcfdf9;
      box-shadow: 0 24px 60px rgba(15, 41, 34, 0.12);
      overflow: hidden;
    }
    .receipt-header {
      display: grid;
      grid-template-columns: minmax(0, 1fr) auto;
      gap: 24px;
      align-items: start;
      border-bottom: 1px solid #d7dfd5;
      background:
        linear-gradient(135deg, rgba(31, 111, 104, 0.14), rgba(255, 247, 223, 0.32)),
        #f8fbf5;
      padding: 28px 32px;
    }
    .receipt-kicker,
    dt,
    th {
      color: #1f6f68;
      font-size: 11px;
      font-weight: 800;
      letter-spacing: 0.08em;
      text-transform: uppercase;
    }
    h1 {
      margin: 7px 0 8px;
      font-size: 30px;
      line-height: 1.05;
    }
    .receipt-header p,
    .muted {
      color: #5d6d62;
      margin: 0;
    }
    .status-pill {
      display: inline-flex;
      border: 1px solid rgba(31, 111, 104, 0.22);
      border-radius: 999px;
      background: rgba(31, 111, 104, 0.1);
      color: #164842;
      font-size: 12px;
      font-weight: 800;
      padding: 7px 10px;
      white-space: nowrap;
      text-transform: capitalize;
    }
    .receipt-total {
      text-align: right;
    }
    .receipt-total strong {
      display: block;
      color: #18231c;
      font-size: 32px;
      line-height: 1;
      margin-top: 10px;
    }
    .receipt-body {
      display: grid;
      gap: 24px;
      padding: 28px 32px 32px;
    }
    .detail-grid {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 14px 22px;
    }
    dl {
      display: contents;
    }
    dt {
      margin-bottom: 5px;
    }
    dd {
      margin: 0;
      color: #25352b;
      font-size: 14px;
      overflow-wrap: anywhere;
    }
    .detail-card {
      border-top: 1px solid #d7dfd5;
      padding-top: 12px;
    }
    table {
      width: 100%;
      border-collapse: collapse;
      border: 1px solid #d7dfd5;
      border-radius: 8px;
      overflow: hidden;
    }
    th,
    td {
      border-bottom: 1px solid #d7dfd5;
      padding: 13px 14px;
      text-align: left;
      vertical-align: top;
    }
    th:last-child,
    td:last-child {
      text-align: right;
    }
    td strong,
    td span {
      display: block;
    }
    td span {
      color: #6a776e;
      font-size: 12px;
      margin-top: 4px;
    }
    tfoot td {
      border-bottom: 0;
      font-size: 16px;
      font-weight: 800;
    }
    .receipt-footer {
      border-top: 1px solid #d7dfd5;
      color: #6a776e;
      font-size: 12px;
      line-height: 1.5;
      padding-top: 16px;
    }
    @media print {
      body {
        background: #ffffff;
        padding: 0;
      }
      .receipt {
        box-shadow: none;
        border-radius: 0;
        max-width: none;
      }
    }
    @media (max-width: 640px) {
      body {
        padding: 16px;
      }
      .receipt-header,
      .detail-grid {
        grid-template-columns: 1fr;
      }
      .receipt-total {
        text-align: left;
      }
    }
  </style>
</head>
<body>
  <main class="receipt">
    <section class="receipt-header">
      <div>
        <span class="receipt-kicker">${escapeHtml(rangeName)}</span>
        <h1>${escapeHtml(receipt.description || title)}</h1>
        <p>${escapeHtml(receiptTypeLabel(receipt.type))} through ${escapeHtml(receipt.provider ? receipt.provider.toUpperCase() : "payment provider")}</p>
      </div>
      <div class="receipt-total">
        <span class="status-pill">${escapeHtml(status)}</span>
        <strong>${escapeHtml(total)}</strong>
      </div>
    </section>
    <section class="receipt-body">
      <div class="detail-grid">
        <div class="detail-card"><dt>Receipt number</dt><dd>${escapeHtml(receipt.receiptId || "Not recorded")}</dd></div>
        <div class="detail-card"><dt>Paid at</dt><dd>${escapeHtml(paidAt)}</dd></div>
        <div class="detail-card"><dt>Customer email</dt><dd>${escapeHtml(customerEmail)}</dd></div>
        <div class="detail-card"><dt>Payment mode</dt><dd>${escapeHtml(receipt.mode || "checkout")}</dd></div>
        <div class="detail-card"><dt>Checkout session</dt><dd>${escapeHtml(compactReference(receipt.checkoutSessionId))}</dd></div>
        <div class="detail-card"><dt>Payment intent</dt><dd>${escapeHtml(compactReference(receipt.paymentIntentId || receipt.subscriptionId || receipt.customerId))}</dd></div>
      </div>
      <table>
        <thead>
          <tr>
            <th>Item</th>
            <th>Amount</th>
          </tr>
        </thead>
        <tbody>
          ${lineItems}
        </tbody>
        <tfoot>
          <tr>
            <td>Total paid</td>
            <td>${escapeHtml(total)}</td>
          </tr>
        </tfoot>
      </table>
      <div class="receipt-footer">
        This receipt was generated from RangeGrid owner records. Stripe remains the payment processor of record for card network and payout details.
      </div>
    </section>
  </main>
</body>
</html>`;
}

export function downloadReceiptDocument(receipt, options = {}) {
  const normalizedReceipt = normalizeReceipt(receipt);
  const html = renderReceiptHtml(normalizedReceipt, options);
  const blob = new Blob([html], { type: "text/html;charset=utf-8" });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = receiptFilename(normalizedReceipt);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}
